import { useState } from 'react'
import { format } from 'date-fns'
import { th } from 'date-fns/locale'
import PageContainer from '@/common/components/PageContainer'
import withUserPermission from '@/common/components/WithUserPermission'
import { PermissionType } from '@/common/types'
import { PermissionKeys } from '@/modules/manage-member/mappers'
import { useDateFilter } from '@/common/hooks/useDateFilter'
import Heading from '@/common/shared/heading'
import { Separator } from '@/common/ui/separator'
import { Badge } from '@/common/ui/badge'
import { Button } from '@/common/ui/button'
import { Label } from '@/common/ui/label'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/common/ui/select'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/common/ui/table'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '@/common/ui/dialog'
import { Skeleton } from '@/common/ui/skeleton'
import { ChevronLeft, ChevronRight, Eye } from 'lucide-react'
import { DateRangePicker } from '@/common/components/DateRangePicker'
import DateRangeButtons from '@/common/components/DateRangeButtons'
import useQueryGetChannelTransactions from '../../api/hooks/useQueryGetChannelTransactions'
import {
	MessageChannel,
	SmsTransactionStatus,
	SmsTransactionWithCampaign,
	LineTransactionWithCampaign,
	LineErrorType,
} from '../../types'

import type { components } from '@/schema'

type PlayerForTransaction = components['schemas']['PlayerForTransactionModel']

const PAGE_SIZE = 10

const ChannelTransactionsPage = () => {
	const [selectedChannel, setSelectedChannel] = useState<MessageChannel>(
		MessageChannel.SMS,
	)
	const [page, setPage] = useState(0)
	const [selectedPlayers, setSelectedPlayers] = useState<
		PlayerForTransaction[] | null
	>(null)
	const { dateFilter } = useDateFilter()

	const { data, isLoading } = useQueryGetChannelTransactions({
		skip: page * PAGE_SIZE,
		take: PAGE_SIZE,
		channel: selectedChannel,
		startDate: dateFilter.startDate || undefined,
		endDate: dateFilter.endDate || undefined,
	})

	const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 0

	const handleChannelChange = (channel: string) => {
		setSelectedChannel(channel as MessageChannel)
		setPage(0) // Reset to first page when changing channel
	}

	const getSmsStatusBadge = (status: SmsTransactionStatus) => {
		switch (status) {
			case SmsTransactionStatus.PENDING:
				return <Badge variant="secondary">กำลังดำเนินการ</Badge>
			case SmsTransactionStatus.SUCCESS:
				return <Badge variant="default">ส่งสำเร็จ</Badge>
			case SmsTransactionStatus.FAILED:
				return <Badge variant="destructive">ล้มเหลว</Badge>
			default:
				return <Badge>{status}</Badge>
		}
	}

	const getLineErrorBadge = (error: string | undefined) => {
		if (!error) {
			return '-'
		}

		switch (error) {
			case LineErrorType.GENERAL:
				return (
					<Badge variant="destructive" className="whitespace-nowrap">
						ข้อผิดพลาดทั่วไป
					</Badge>
				)
			case LineErrorType.EXCEED_QUOTA_LIMIT:
				return (
					<Badge
						variant="secondary"
						className="whitespace-nowrap bg-orange-100 text-orange-800 hover:bg-orange-200"
					>
						เกินโควต้า
					</Badge>
				)
			default:
				return <Badge variant="outline">{error}</Badge>
		}
	}

	return (
		<PageContainer>
			<div className="space-y-4 px-4 sm:px-0">
				<div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
					<Heading
						title="ประวัติการส่งข้อความ"
						description="ดูรายละเอียดการส่งข้อความแยกตามช่องทาง"
					/>

					<div className="flex flex-col gap-3 sm:items-end sm:gap-4">
						<div className="flex items-center gap-3">
							<Label className="whitespace-nowrap">ช่องทาง:</Label>
							<Select
								value={selectedChannel}
								onValueChange={handleChannelChange}
							>
								<SelectTrigger className="w-[180px]">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value={MessageChannel.SMS}>SMS</SelectItem>
									<SelectItem value={MessageChannel.LINE}>LINE</SelectItem>
								</SelectContent>
							</Select>
						</div>
						<DateRangePicker />
						<DateRangeButtons />
					</div>
				</div>

				<Separator className="my-4 sm:my-6" />

				{isLoading ? (
					<div className="space-y-3">
						{[...Array(5)].map((_, i) => (
							<Skeleton key={i} className="h-12 w-full" />
						))}
					</div>
				) : (
					<>
						<div className="rounded-md border">
							<Table>
								<TableHeader>
									<TableRow>
										{selectedChannel === MessageChannel.SMS ? (
											<>
												<TableHead>ข้อความ</TableHead>
												<TableHead>ผู้รับ</TableHead>
												<TableHead>สถานะ</TableHead>
												<TableHead>จำนวนส่งสำเร็จ</TableHead>
												<TableHead>เครดิตที่ใช้</TableHead>
												<TableHead>ข้อผิดพลาด</TableHead>
												<TableHead>วันที่สร้าง</TableHead>
											</>
										) : (
											<>
												<TableHead>ข้อความ</TableHead>
												<TableHead>ผู้รับ</TableHead>
												<TableHead>จำนวนส่งสำเร็จ</TableHead>
												<TableHead>จำนวนล้มเหลว</TableHead>
												<TableHead>ข้อผิดพลาด</TableHead>
												<TableHead>วันที่สร้าง</TableHead>
											</>
										)}
									</TableRow>
								</TableHeader>
								<TableBody>
									{data?.data && data.data.length > 0 ? (
										data.data.map((transaction) => {
											// Type casting based on channel
											// Priority: messageTransaction > autoMessageTransaction > intervalMessageTransaction
											const campaignInfo =
												'messageTransaction' in transaction &&
												transaction.messageTransaction
													? transaction.messageTransaction
													: transaction.autoMessageTransaction ||
														transaction.intervalMessageTransaction

											if (selectedChannel === MessageChannel.SMS) {
												const smsTransaction =
													transaction as SmsTransactionWithCampaign
												const hasMultiplePlayers =
													smsTransaction.players.length > 1
												const singlePhone =
													smsTransaction.players.length === 1
														? smsTransaction.players[0].phone
														: null

												return (
													<TableRow key={smsTransaction.id}>
														<TableCell className="max-w-xs">
															<div
																className="truncate"
																title={campaignInfo?.message || '-'}
															>
																{campaignInfo?.message || '-'}
															</div>
														</TableCell>
														<TableCell>
															{hasMultiplePlayers ? (
																<Button
																	variant="ghost"
																	size="icon"
																	onClick={() =>
																		setSelectedPlayers(smsTransaction.players)
																	}
																>
																	<Eye className="h-4 w-4" />
																</Button>
															) : (
																<span className="font-mono">
																	{singlePhone || '-'}
																</span>
															)}
														</TableCell>
														<TableCell>
															{getSmsStatusBadge(smsTransaction.status)}
														</TableCell>
														<TableCell>
															{smsTransaction.successCount ?? 0}
														</TableCell>
														<TableCell>
															{smsTransaction.creditUsed ?? 0}
														</TableCell>
														<TableCell>-</TableCell>
														<TableCell>
															{format(
																new Date(smsTransaction.createdAt),
																'dd MMM yyyy HH:mm',
																{ locale: th },
															)}
														</TableCell>
													</TableRow>
												)
											} else {
												const lineTransaction =
													transaction as LineTransactionWithCampaign & {
														error?: string
													}
												const hasMultiplePlayers =
													lineTransaction.players.length > 1
												const singlePhone =
													lineTransaction.players.length === 1
														? lineTransaction.players[0].phone
														: null

												return (
													<TableRow key={lineTransaction.id}>
														<TableCell className="max-w-xs">
															<div
																className="truncate"
																title={campaignInfo?.message || '-'}
															>
																{campaignInfo?.message || '-'}
															</div>
														</TableCell>
														<TableCell>
															{hasMultiplePlayers ? (
																<Button
																	variant="ghost"
																	size="icon"
																	onClick={() =>
																		setSelectedPlayers(lineTransaction.players)
																	}
																>
																	<Eye className="h-4 w-4" />
																</Button>
															) : (
																<span className="font-mono">
																	{singlePhone || '-'}
																</span>
															)}
														</TableCell>
														<TableCell>
															{lineTransaction.successfulPlayerIds?.length ?? 0}
														</TableCell>
														<TableCell>
															{lineTransaction.failedPlayerIds?.length ?? 0}
														</TableCell>
														<TableCell>
															{getLineErrorBadge(lineTransaction.error)}
														</TableCell>
														<TableCell>
															{format(
																new Date(lineTransaction.createdAt),
																'dd MMM yyyy HH:mm',
																{ locale: th },
															)}
														</TableCell>
													</TableRow>
												)
											}
										})
									) : (
										<TableRow>
											<TableCell
												colSpan={selectedChannel === MessageChannel.SMS ? 7 : 6}
												className="text-center"
											>
												ไม่พบข้อมูล
											</TableCell>
										</TableRow>
									)}
								</TableBody>
							</Table>
						</div>

						{/* Pagination */}
						{totalPages > 1 && (
							<div className="mt-4 flex items-center justify-between">
								<div className="text-sm text-muted-foreground">
									หน้า {page + 1} จาก {totalPages}
								</div>
								<div className="flex gap-2">
									<Button
										variant="outline"
										size="sm"
										onClick={() => setPage((p) => Math.max(0, p - 1))}
										disabled={page === 0}
									>
										<ChevronLeft className="h-4 w-4" />
										ก่อนหน้า
									</Button>
									<Button
										variant="outline"
										size="sm"
										onClick={() => setPage((p) => p + 1)}
										disabled={page >= totalPages - 1}
									>
										ถัดไป
										<ChevronRight className="h-4 w-4" />
									</Button>
								</div>
							</div>
						)}
					</>
				)}
			</div>

			{/* Recipients Modal */}
			<Dialog
				open={selectedPlayers !== null}
				onOpenChange={(open) => !open && setSelectedPlayers(null)}
			>
				<DialogContent className="max-w-md">
					<DialogHeader>
						<DialogTitle>รายชื่อผู้รับ</DialogTitle>
						<DialogDescription>
							รายการเบอร์โทรศัพท์ทั้งหมดที่ได้รับข้อความ (
							{selectedPlayers?.length || 0} คน)
						</DialogDescription>
					</DialogHeader>
					<div className="max-h-96 overflow-y-auto">
						<div className="space-y-2">
							{selectedPlayers?.map((player, index) => (
								<div
									key={player.id}
									className="flex items-center gap-3 rounded-lg border p-3"
								>
									<div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-muted text-sm font-medium">
										{index + 1}
									</div>
									<span className="font-mono">{player.phone}</span>
								</div>
							))}
						</div>
					</div>
				</DialogContent>
			</Dialog>
		</PageContainer>
	)
}

const GuardedChannelTransactionsPage = withUserPermission(
	ChannelTransactionsPage,
	{
		permissionType: PermissionType.Read,
		permissionKey: [PermissionKeys.MANAGE_SMS],
	},
)

export { GuardedChannelTransactionsPage as ChannelTransactionsPage }
